/** Strict package-only suggestions; never changes the permissive ordinary editor normalizer. */
import {
  PROFESSIONAL_GRADERS,
  PROFESSIONAL_GRADES,
  RAW_CARD_CONDITIONS,
  type CardPresentation,
} from './card-presentation';

export const BATCH_TRANSFER_CERTIFICATION_MAX_LENGTH = 30;
// eslint-disable-next-line no-control-regex -- The package contract explicitly rejects C0/C1 controls.
const controls = /[\u0000-\u001f\u007f-\u009f]/;
// ECMAScript trim whitespace, explicitly spelled out for portable validators (e.g. Python).
const trimWhitespace =
  '\\u0009-\\u000d\\u0020\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff';
// The two alternatives recognize astral characters with or without a Unicode regex flag.
const astral = '(?:[\\uD800-\\uDBFF][\\uDC00-\\uDFFF]|[^\\u0000-\\uFFFF])';
/** JSON Schema maxLength counts code points; these finite rules enforce the existing UTF-16 limit. */
export const BATCH_TRANSFER_CERTIFICATION_SCHEMA = {
  type: 'string',
  minLength: 1,
  maxLength: BATCH_TRANSFER_CERTIFICATION_MAX_LENGTH,
  pattern: `[^${trimWhitespace}]`,
  not: { pattern: '[\\u0000-\\u001f\\u007f-\\u009f]' },
  allOf: Array.from({ length: BATCH_TRANSFER_CERTIFICATION_MAX_LENGTH / 2 }, (_, index) => ({
    if: { pattern: `^(?=[\\s\\S]{0,60}$)(?:[\\s\\S]*?${astral}){${index + 1}}` },
    then: { maxLength: BATCH_TRANSFER_CERTIFICATION_MAX_LENGTH - index - 1 },
  })),
};

export type BatchTransferPresentationParseResult =
  | { ok: true; value: CardPresentation }
  | { ok: false; error: string };

/** Field paths identify malformed data without copying untrusted values into diagnostics. */
export function parseBatchTransferPresentation(
  value: unknown,
  path = 'listing.presentation',
): BatchTransferPresentationParseResult {
  const invalid = (field = ''): BatchTransferPresentationParseResult => ({
    ok: false,
    // Do not reflect unbounded/control-bearing arbitrary property names into diagnostic reports.
    error: `Invalid ${path}${/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(field) ? `.${field}` : ''}.`,
  });
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return invalid();
  const candidate = value as Record<string, unknown>;
  if (candidate['kind'] !== 'RAW' && candidate['kind'] !== 'GRADED') return invalid('kind');
  const allowed =
    candidate['kind'] === 'RAW'
      ? ['kind', 'condition']
      : ['kind', 'grader', 'grade', 'certificationNumber'];
  const unknown = Object.keys(candidate).find((key) => !allowed.includes(key));
  if (unknown !== undefined) return invalid(unknown);
  if (candidate['kind'] === 'RAW') {
    if (!RAW_CARD_CONDITIONS.includes(candidate['condition'] as never)) return invalid('condition');
    return {
      ok: true,
      value: {
        kind: 'RAW',
        condition: candidate['condition'] as (typeof RAW_CARD_CONDITIONS)[number],
      },
    };
  }
  if (!PROFESSIONAL_GRADERS.includes(candidate['grader'] as never)) return invalid('grader');
  if (!PROFESSIONAL_GRADES.includes(candidate['grade'] as never)) return invalid('grade');
  const cert = candidate['certificationNumber'];
  const hasCert = Object.prototype.hasOwnProperty.call(candidate, 'certificationNumber');
  if (
    hasCert &&
    (typeof cert !== 'string' ||
      cert.length > BATCH_TRANSFER_CERTIFICATION_MAX_LENGTH ||
      cert.trim() === '' ||
      controls.test(cert))
  )
    return invalid('certificationNumber');
  return {
    ok: true,
    value: {
      kind: 'GRADED',
      grader: candidate['grader'] as (typeof PROFESSIONAL_GRADERS)[number],
      grade: candidate['grade'] as (typeof PROFESSIONAL_GRADES)[number],
      ...(typeof cert === 'string' ? { certificationNumber: cert.trim() } : {}),
    },
  };
}
